'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { createElement, useCallback, useEffect } from 'react';
import { LOCALE } from 'utils';

import { useCurrentLocale } from '@/shared/utils/client';

import { removeBasketBindingApiKeyToLocalStorage } from '../hooks/use-basket-binding-api-key.utils';
import { WidgetBasketEvent, WidgetBindingPlace } from '../types/types';
import { InpostIziButtonProps } from './inpost-izi-button.type';

export function InpostIziButton({
  bindingPlace,
  productId,
  variation = '',
  basketBindingApiKey,
  onUnboundWidgetClicked,
  basketId,
}: InpostIziButtonProps) {
  const locale = useCurrentLocale();
  const router = useRouter();
  const searchParams = useSearchParams();

  const handleBasketEvent = useCallback(
    (event: WidgetBasketEvent) => {
      if (event === 'orderCreated') {
        removeBasketBindingApiKeyToLocalStorage();

        router.push('/checkout/status/inpost-pay');
        return true;
      }

      return false;
    },
    [router]
  );

  const handleScriptReady = useCallback(() => {
    delete window.__INPOST_PAY_WIDGET__;

    const options = {
      merchantClientId:
        process.env.NEXT_PUBLIC_IN_POST_MERCHANT_CLIENT_ID || '',
      basketBindingApiKey: basketBindingApiKey,
      unboundWidgetClicked: onUnboundWidgetClicked,
      handleBasketEvent: handleBasketEvent,
    };

    if (window.__INPOST_PAY_WIDGET__ === undefined) {
      window.FINESIO_INPOST_PAY_WIDGET = window.InPostPayWidget.init(options);
    } else {
      window.FINESIO_INPOST_PAY_WIDGET?.refresh(options);
    }
  }, [basketBindingApiKey, handleBasketEvent, onUnboundWidgetClicked]);

  useEffect(() => {
    if (!window.__INPOST_PAY_WIDGET__ || !basketBindingApiKey) {
      return;
    }

    handleScriptReady();
  }, [basketBindingApiKey, handleScriptReady]);

  if (locale !== LOCALE.PL) {
    return null;
  }

  let props: Record<string, string> = {
    binding_place: bindingPlace,
    variation,
  };

  if (productId) {
    props = {
      ...props,
      'data-product-id': productId,
    };
  }

  return (
    <>
      <Script
        src={process.env.NEXT_PUBLIC_IN_POST_WDIGET_URL}
        id="inpost-pay-widgate-v2"
        strategy="afterInteractive"
        onReady={handleScriptReady}
      />

      {bindingPlace === WidgetBindingPlace.THANK_YOU_PAGE ? (
        <inpost-thank-you />
      ) : null}

      {bindingPlace !== WidgetBindingPlace.THANK_YOU_PAGE
        ? createElement('inpost-izi-button', props)
        : null}
    </>
  );
}
